import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Linking, Platform, StyleSheet, TextInput as RNTextInput, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { Icon, Text } from '@components'
import { useLanguage, usePaywall } from '@context'
import {
  usePremium,
  useUnipileAccounts,
  useUnipileConnect,
  useUnipileDisconnect,
  useUnipileSyncContacts,
} from '@hooks'
import type { UnipileService } from '@hooks'
import { isPlanLimitError, showErrorToast, showInfoToast, showSuccessToast, showToast } from '@utils'
import { Theme } from '@theme'

const SERVICES: { key: UnipileService; icon: 'instagram' | 'messageCircle' | 'linkedin' | 'google'; labelKey: string }[] = [
  { key: 'instagram', icon: 'instagram', labelKey: 'connectedServices.serviceInstagram' },
  { key: 'whatsapp', icon: 'messageCircle', labelKey: 'connectedServices.serviceWhatsapp' },
  { key: 'linkedin', icon: 'linkedin', labelKey: 'connectedServices.serviceLinkedin' },
  { key: 'gmail', icon: 'google', labelKey: 'connectedServices.serviceGmail' },
]

const openWizard = (url: string) => {
  if (Platform.OS === 'web') {
    window.open(url, '_blank', 'noopener')
  } else {
    Linking.openURL(url)
  }
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    description: {
      color: theme.colors.secondary,
      marginBottom: theme.spacing.sm,
    },
    serviceCard: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      marginTop: theme.spacing.sm,
    },
    serviceLabel: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      flex: 1,
      marginRight: theme.spacing.md,
    },
    connectedBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    filterInput: {
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: 12,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.lg,
      marginTop: theme.spacing.md,
      color: theme.colors.text ?? '#000',
    },
  })

const ACCOUNTS_POLL_MS = 5_000
const CONTACTS_POLL_MS = 20_000
const BACKGROUND_IMPORT_WATCH_MS = 5 * 60_000

export const ConnectedServices: React.FC = () => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const { t } = useLanguage()

  const { data, isFetching, refetch } = useUnipileAccounts()
  const { mutate: connect, isPending: isConnecting } = useUnipileConnect()
  const { mutate: syncContacts, isPending: isSyncing } = useUnipileSyncContacts()
  const { mutate: disconnect, isPending: isDisconnecting } = useUnipileDisconnect()
  // Connecting services and importing contacts is a Premium feature: free
  // accounts are routed to the paywall (the backend enforces it too, 402).
  const { isPremium } = usePremium()
  const { showPaywall } = usePaywall()
  const [sectorFilter, setSectorFilter] = useState('')
  const queryClient = useQueryClient()
  // After the connect wizard opens we poll the accounts until the new service
  // shows up; the backend then imports its contacts in the background (Unipile
  // webhook), so we keep a hint on screen and refresh the contacts meanwhile.
  const [awaitingConnectionUntil, setAwaitingConnectionUntil] = useState<number | null>(null)
  const [backgroundImportUntil, setBackgroundImportUntil] = useState<number | null>(null)
  const knownServices = useRef<Set<string> | null>(null)

  const handleDisconnect = (unipileAccountId: string) => {
    disconnect(unipileAccountId, {
      onSuccess: () => showSuccessToast(t('connectedServices.disconnected')),
      onError: () => showErrorToast(t('connectedServices.disconnectError')),
    })
  }

  const accounts = data?.accounts ?? []

  useEffect(() => {
    if (!data) return
    const services = new Set(data.accounts.map((account) => account.service))
    const previous = knownServices.current
    knownServices.current = services
    if (!previous || ![...services].some((service) => !previous.has(service))) return
    setAwaitingConnectionUntil(null)
    setBackgroundImportUntil(Date.now() + BACKGROUND_IMPORT_WATCH_MS)
    showToast({ type: 'info', title: t('connectedServices.syncStarted'), visibilityTime: 6000 })
  }, [data, t])

  useEffect(() => {
    if (!awaitingConnectionUntil) return
    const timer = setInterval(() => {
      if (Date.now() > awaitingConnectionUntil) setAwaitingConnectionUntil(null)
      else refetch()
    }, ACCOUNTS_POLL_MS)
    return () => clearInterval(timer)
  }, [awaitingConnectionUntil, refetch])

  useEffect(() => {
    if (!backgroundImportUntil) return
    const timer = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      if (Date.now() > backgroundImportUntil) setBackgroundImportUntil(null)
    }, CONTACTS_POLL_MS)
    return () => clearInterval(timer)
  }, [backgroundImportUntil, queryClient])
  const accountFor = (service: UnipileService) =>
    accounts.find((account) => account.service === service)

  const handleConnect = (service: UnipileService) => {
    if (!isPremium) {
      showPaywall()
      return
    }
    connect(service, {
      onSuccess: ({ url }) => {
        openWizard(url)
        showInfoToast(t('connectedServices.wizardOpened'))
        setAwaitingConnectionUntil(Date.now() + BACKGROUND_IMPORT_WATCH_MS)
      },
      onError: (error) =>
        isPlanLimitError(error)
          ? showPaywall()
          : showErrorToast(t('connectedServices.wizardError')),
    })
  }

  const handleSync = () => {
    if (!isPremium) {
      showPaywall()
      return
    }
    // The first LinkedIn import can take a few minutes — say it upfront so
    // the wait does not look like a crash (users were disconnecting and
    // reconnecting their account in the meantime).
    showToast({ type: 'info', title: t('connectedServices.syncStarted'), visibilityTime: 6000 })
    syncContacts({ sectorFilter: sectorFilter.trim() || undefined }, {
      onSuccess: ({ reports }) => {
        const created = reports.reduce((sum, report) => sum + report.created, 0)
        const skipped = reports.reduce(
          (sum, report) => sum + report.skippedExisting,
          0,
        )
        // Provider failures come back as per-report errors, not HTTP errors.
        if (reports.some((report) => report.error) && created === 0) {
          showErrorToast(t('connectedServices.syncError'))
          return
        }
        const filtered = reports.reduce(
          (sum, report) => sum + (report.skippedFiltered ?? 0),
          0,
        )
        if (created === 0 && skipped === 0 && filtered === 0) {
          showInfoToast(t('connectedServices.syncEmpty'))
        } else {
          showSuccessToast(
            t('connectedServices.syncResult', { created, skipped }),
          )
        }
        if (filtered > 0) {
          showInfoToast(t('connectedServices.syncFilteredOut', { filtered }))
        }
        if (reports.some((report) => report.planLimitReached)) {
          showErrorToast(t('connectedServices.planLimit'))
        }
      },
      onError: (error) =>
        isPlanLimitError(error)
          ? showPaywall()
          : showErrorToast(t('connectedServices.syncError')),
    })
  }

  return (
    <View>
      <View style={styles.header}>
        <Text type="bodySmall" color={theme.colors.lightGrey}>
          {t('connectedServices.sectionTitle')}
        </Text>
        <Icon
          testID="connected-services-refresh"
          name="refresh"
          size={18}
          color={theme.colors.primary}
          onPress={() => refetch()}
        />
      </View>

      {SERVICES.map((service) => {
        const account = accountFor(service.key)
        const isConnected = account?.status === 'OK'
        const needsReconnect =
          account !== undefined && account.status === 'CREDENTIALS'

        return (
          <View
            key={service.key}
            style={styles.serviceCard}
            testID={`connected-services-${service.key}`}
          >
            <View style={styles.serviceLabel}>
              <Icon name={service.icon} size={20} color={theme.colors.primary} />
              <Text type="body">{t(service.labelKey)}</Text>
            </View>

            {isConnected ? (
              <View style={styles.connectedBadge}>
                <Icon name="check" size={18} color={theme.colors.primary} />
                <Text type="bodySmall" style={{ color: theme.colors.primary }}>
                  {t('connectedServices.connected')}
                </Text>
                <TouchableOpacity
                  testID={`connected-services-disconnect-${service.key}`}
                  onPress={() => account && handleDisconnect(account.id)}
                  disabled={isDisconnecting}
                  activeOpacity={0.7}
                  style={{ marginLeft: theme.spacing.md }}
                >
                  <Text type="bodySmall" style={{ color: theme.colors.error, textDecorationLine: 'underline' }}>
                    {t('connectedServices.disconnect')}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                testID={`connected-services-connect-${service.key}`}
                onPress={() => handleConnect(service.key)}
                disabled={isConnecting}
                activeOpacity={0.7}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
              >
                {!isPremium && (
                  <Icon name="star" size={16} color={theme.colors.gold} />
                )}
                <Text type="bodyBold" style={{ color: theme.colors.primary }}>
                  {account && !needsReconnect
                    ? t('connectedServices.statusConnecting')
                    : needsReconnect
                      ? t('connectedServices.reconnect')
                      : t('connectedServices.connect')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )
      })}

      <RNTextInput
        testID="connected-services-filter"
        value={sectorFilter}
        onChangeText={setSectorFilter}
        placeholder={t('connectedServices.filterPlaceholder')}
        placeholderTextColor={theme.colors.lightGrey}
        style={styles.filterInput}
      />

      <TouchableOpacity
        testID="connected-services-sync"
        style={styles.serviceCard}
        onPress={handleSync}
        // Free accounts can always tap: the tap routes to the paywall.
        disabled={isSyncing || (isPremium && accounts.length === 0)}
        activeOpacity={0.7}
      >
        <View style={styles.serviceLabel}>
          <Icon name="userPlus" size={20} color={theme.colors.primary} />
          <Text type="body">
            {isSyncing
              ? t('connectedServices.syncing')
              : t('connectedServices.syncNow')}
          </Text>
        </View>
        {!isPremium && <Icon name="star" size={18} color={theme.colors.gold} />}
        {(isSyncing || isFetching) && (
          <ActivityIndicator size="small" color={theme.colors.primary} />
        )}
      </TouchableOpacity>

      {(isSyncing || backgroundImportUntil !== null) && (
        <Text
          type="bodySmall"
          color={theme.colors.lightGrey}
          style={{ marginTop: theme.spacing.sm }}
          testID="connected-services-sync-hint"
        >
          {t('connectedServices.syncInProgressHint')}
        </Text>
      )}

      {accounts.length === 0 && (
        <Text
          type="bodySmall"
          color={theme.colors.lightGrey}
          style={{ marginTop: theme.spacing.sm }}
        >
          {t('connectedServices.noAccounts')}
        </Text>
      )}
    </View>
  )
}
